import React from 'react';
import { ArrowLeft, Zap } from 'lucide-react';
import { dateKey } from '../../lib/availability';

/**
 * "Choose a time" step — the horizontal date strip + slot grid.
 * Also renders the three empty states (no hours / fully booked /
 * loading) inline.
 */
export const TimeStep = ({
  barber,
  showBarberStep,
  availabilityLoading,
  emptyReason,
  dayAvailability,
  selectedDate,
  availableSlots,
  onBack,
  onDateSelect,
  onSlotSelect,
  onPickNextAvailable,
}) => {
  const firstAvail = dayAvailability.find((d) => d.hasSlots && d.slots.length > 0);
  return (
    <div className="booking-step">
      <button className="back-btn" onClick={onBack}>
        <ArrowLeft size={16} /> Back
      </button>
      <h2>Choose a time</h2>
      <p className="step-subtitle">{barber?.name}</p>

      {availabilityLoading && (
        <div className="no-slots" data-testid="availability-loading">
          Checking availability&hellip;
        </div>
      )}

      {!availabilityLoading && emptyReason === 'no_hours' && (
        <div className="no-slots" data-testid="empty-no-hours">
          <p>
            <strong>{barber?.name?.split(' ')[0] || 'This barber'}</strong> hasn&apos;t
            added their working hours yet.
          </p>
          <p>Please contact the shop, or {showBarberStep ? 'choose another barber above.' : 'try again later.'}</p>
        </div>
      )}

      {!availabilityLoading && emptyReason === 'fully_booked' && (
        <div className="no-slots" data-testid="empty-fully-booked">
          <p>
            <strong>{barber?.name?.split(' ')[0] || 'This barber'}</strong> is fully
            booked for the next 2 weeks.
          </p>
          <p>Please contact the shop{showBarberStep ? ' or choose another barber above.' : ' to be added to the waitlist.'}</p>
        </div>
      )}

      {!availabilityLoading && !emptyReason && dayAvailability.length > 0 && (
        <>
          <div className="date-strip" data-testid="date-strip">
            {firstAvail && (
              <button
                type="button"
                className="date-chip date-chip-next"
                onClick={onPickNextAvailable}
                data-testid="date-chip-next-available"
                title={`${firstAvail.weekday} ${firstAvail.dayNum} ${firstAvail.monthShort} · ${firstAvail.slots[0].display}`}
              >
                <span className="date-chip-wk">
                  <Zap size={11} strokeWidth={2.5} style={{ verticalAlign: '-1px', marginRight: 3 }} />
                  Next
                </span>
                <span className="date-chip-day">{firstAvail.dayNum}</span>
                <span className="date-chip-mo">{firstAvail.slots[0].display}</span>
              </button>
            )}
            {dayAvailability.map((d) => {
              const isSelected = selectedDate && d.key === dateKey(selectedDate);
              const disabled = !d.hasSlots;
              return (
                <button
                  key={d.key}
                  type="button"
                  className={`date-chip${isSelected ? ' is-selected' : ''}${disabled ? ' is-disabled' : ''}`}
                  onClick={() => !disabled && onDateSelect(d.date)}
                  disabled={disabled}
                  data-testid={`date-chip-${d.key}`}
                  aria-pressed={isSelected}
                >
                  <span className="date-chip-wk">{d.weekday}</span>
                  <span className="date-chip-day">{d.dayNum}</span>
                  <span className="date-chip-mo">{d.monthShort}</span>
                </button>
              );
            })}
          </div>

          {availableSlots.length === 0 ? (
            <div className="no-slots" data-testid="empty-day-no-slots">
              <p>No slots on this day. Pick a different date above.</p>
            </div>
          ) : (
            <div className="time-slots" data-testid="time-slots">
              {availableSlots.map((slot) => (
                <button
                  key={slot.time}
                  className="time-slot"
                  onClick={() => onSlotSelect(slot)}
                  data-testid={`time-slot-${slot.time}`}
                >
                  {slot.display}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
